import { Room } from '@mui/icons-material'
import { Box, Checkbox, List, ListItem, Stack, TextField, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  CarrierVisitStatusChip,
  ConfirmationDialog,
  TomAutoComplete,
  TrainIcon,
  VesselIcon,
} from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { useYardStores } from '@yard/AppProvider'
import {
  AllocationRuleDto,
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitResponse,
} from '@yard/app/api'
import {
  mapAllocationRuleDestinationToYardPositionDescriptor,
  mapAllocationRuleFacetsToString,
} from '@yard/components/AllocationRules/helpers/allocation-rule.mapper'
import { CarrierVisitAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, HTMLAttributes, useState } from 'react'

interface Props {
  allocationRulesViewStore: CarrierVisitAllocationRulesViewStore
}

const formatVisitTimestamp = (visit: CarrierVisitResponse): string => {
  const iso = visit.eta || visit.ata || visit.etd || visit.atd
  if (!iso) return ''
  return dayjs(iso).format('HH:mm, ddd DD/MM')
}

export const CarrierVisitCopyRulesDialog: FC<Props> = observer(props => {
  const { allocationRulesViewStore } = props
  const { yardBlockStore } = useYardStores()
  const { t } = useTranslate()

  const [sourceVisitId, setSourceVisitId] = useState<number | undefined>(undefined)
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(new Set())
  const [nameEdits, setNameEdits] = useState<Record<string, string>>({})

  const direction = allocationRulesViewStore.selectedCarrierVisitDirection
  const sourceVisit = allocationRulesViewStore.copyableSourceCarrierVisits.find(
    cv => cv.id === sourceVisitId,
  )
  const sourceRules: AllocationRuleDto[] = sourceVisitId
    ? allocationRulesViewStore.copyableSourceVisitRules(sourceVisitId)
    : []

  const hasRules = sourceRules.length > 0
  const selectionCount = selectedRuleIds.size
  const allChecked = hasRules && selectionCount === sourceRules.length
  const someChecked = selectionCount > 0 && !allChecked

  const resetLocalState = () => {
    setSourceVisitId(undefined)
    setSelectedRuleIds(new Set())
    setNameEdits({})
  }

  const handleClose = () => {
    resetLocalState()
    allocationRulesViewStore.toggleCopyFromVisitDialog()
  }

  const handleSourceVisitChange = (cv?: CarrierVisitResponse | null) => {
    setSourceVisitId(cv?.id)
    setSelectedRuleIds(new Set())
    setNameEdits({})
  }

  const seedNameIfMissing = (
    prev: Record<string, string>,
    rule: AllocationRuleDto,
  ): Record<string, string> =>
    prev[rule.id] !== undefined ? prev : { ...prev, [rule.id]: rule.name }

  const handleToggleRule = (rule: AllocationRuleDto) => {
    setSelectedRuleIds(prev => {
      const next = new Set(prev)
      if (next.has(rule.id)) {
        next.delete(rule.id)
      } else {
        next.add(rule.id)
      }
      return next
    })
    setNameEdits(prev => seedNameIfMissing(prev, rule))
  }

  const handleToggleSelectAll = () => {
    if (allChecked) {
      setSelectedRuleIds(new Set())
      return
    }
    setSelectedRuleIds(new Set(sourceRules.map(r => r.id)))
    setNameEdits(prev => {
      let next = prev
      sourceRules.forEach(rule => {
        next = seedNameIfMissing(next, rule)
      })
      return next
    })
  }

  const handleNameChange = (ruleId: string, value: string) => {
    setNameEdits(prev => ({ ...prev, [ruleId]: value }))
  }

  const handleConfirm = () => {
    if (!sourceVisit || selectionCount === 0) return
    const selections = sourceRules
      .filter(r => selectedRuleIds.has(r.id))
      .map(r => ({ rule: r, name: nameEdits[r.id] ?? r.name }))

    allocationRulesViewStore.copyVisitRules(selections)
    handleClose()
    appStore.setShowAlert(
      'success',
      t('rulesCopiedFromVisit', '{count} rules copied from {vessel}.', {
        count: selections.length,
        vessel: sourceVisit.name,
      }),
    )
  }

  const renderVisitOption = (
    optionProps: HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    option: CarrierVisitResponse,
  ) => {
    const { key, ...rest } = optionProps
    const timestamp = formatVisitTimestamp(option)
    return (
      <Box
        component='li'
        key={key ?? option.id}
        {...rest}
        sx={{ display: 'block !important' }}
      >
        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
          <Stack direction='row' alignItems='center' spacing={1} flex={1} minWidth={0}>
            {option.carrierType === CarrierType.Vessel && <VesselIcon fontSize='small' />}
            {option.carrierType === CarrierType.Train && <TrainIcon fontSize='small' />}
            <Typography variant='subtitle2' noWrap>
              {option.name}
            </Typography>
          </Stack>
          <CarrierVisitStatusChip status={option.status} />
        </Stack>
        {timestamp && (
          <Typography variant='caption' color='text.secondary'>
            {timestamp}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <ConfirmationDialog
      open={allocationRulesViewStore.isCopyFromVisitDialogOpen}
      title={t('copyDirectionRulesFromVisit', 'Copy {direction} rules from a visit', {
        direction:
          direction === CarrierVisitDirection.Outbound
            ? t('outbound', 'outbound')
            : t('inbound', 'inbound'),
      })}
      primaryActionText={t('confirm', 'Confirm')}
      primaryActionDisabled={!sourceVisitId || !hasRules || selectionCount === 0}
      closeLabel={t('cancel', 'Cancel')}
      warning={
        sourceVisit &&
        allocationRulesViewStore.selectedCarrierVisit &&
        sourceVisit.carrierType !== allocationRulesViewStore.selectedCarrierVisit.carrierType
          ? t(
              'copyRulesCrossCarrierTypeWarning',
              'The source and target visits have different carrier types. Review the rules carefully before confirming.',
            )
          : undefined
      }
      onCancel={handleClose}
      onClose={handleClose}
      onConfirm={handleConfirm}
      maxWidth='md'
    >
      <Stack spacing={2}>
        <TomAutoComplete<CarrierVisitResponse>
          options={allocationRulesViewStore.copyableSourceCarrierVisits}
          value={sourceVisit ?? null}
          label={t('sourceVisit', 'Source visit')}
          placeholder={t('searchVisitByName', 'Search by visit name')}
          getOptionLabel={cv => cv.name}
          renderOption={renderVisitOption}
          onChangeEntity={cv => handleSourceVisitChange(cv as CarrierVisitResponse | null)}
          enablePortal
        />

        {sourceVisitId && !hasRules && (
          <Box py={2}>
            <Typography color='text.secondary'>
              {direction === CarrierVisitDirection.Outbound
                ? t('visitHasNoOutboundRules', 'This visit has no outbound rules to copy.')
                : t('visitHasNoInboundRules', 'This visit has no inbound rules to copy.')}
            </Typography>
          </Box>
        )}

        {sourceVisitId && hasRules && (
          <>
            <Stack direction='row' alignItems='center'>
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={handleToggleSelectAll}
                slotProps={{
                  input: { 'aria-label': t('selectAllRules', 'Select all rules') },
                }}
              />
              <Typography variant='subtitle2'>{t('selectAll', 'Select all')}</Typography>
            </Stack>

            <Box sx={{ height: '40vh', overflowY: 'auto' }}>
              <List disablePadding>
                {sourceRules.map(rule => {
                  const isChecked = selectedRuleIds.has(rule.id)
                  const destinations = rule.destinations ?? []
                  return (
                    <ListItem key={rule.id} disableGutters alignItems='flex-start'>
                      <Stack direction='row' alignItems='flex-start' spacing={2} width='100%'>
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleRule(rule)}
                          slotProps={{
                            input: {
                              'aria-label': t('selectRuleToCopy', 'Select rule to copy'),
                            },
                          }}
                          sx={{ flex: '0 0 auto', mt: 0.5 }}
                        />
                        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='subtitle2'>{rule.name}</Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {mapAllocationRuleFacetsToString(rule.facets, t, rule.enforcementMode)}
                          </Typography>
                          {destinations.map((destination, index) => (
                            <Stack
                              key={destination.id ?? index}
                              direction='row'
                              alignItems='center'
                              spacing={0.5}
                              width='100%'
                              sx={{ color: 'text.secondary' }}
                            >
                              <Room fontSize='small' />
                              <Typography variant='caption'>
                                {mapAllocationRuleDestinationToYardPositionDescriptor(
                                  yardBlockStore.yardBlocks[destination.blockId],
                                  destination.minBay,
                                  destination.maxBay,
                                  destination.minRow,
                                  destination.maxRow,
                                )}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                        <TextField
                          size='small'
                          disabled={!isChecked}
                          value={nameEdits[rule.id] ?? rule.name}
                          onChange={e => handleNameChange(rule.id, e.target.value)}
                          label={t('newRuleName', 'New rule name')}
                          sx={{ flex: '0 0 40%' }}
                        />
                      </Stack>
                    </ListItem>
                  )
                })}
              </List>
            </Box>
          </>
        )}
      </Stack>
    </ConfirmationDialog>
  )
})
